import "@/App.css";
import "@/styles/admin-theme.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useEffect } from "react";
import * as Sentry from "@sentry/react";
import { RTLWrapper } from "@/components/RTLWrapper";
import { CurrencyProvider } from "@/contexts/CurrencyContext";
import { CartProvider } from "@/contexts/CartContext";
import { StoreSettingsProvider } from "@/contexts/StoreSettingsContext";
import { AuthProvider } from "@/contexts/AuthContext";
import { WishlistProvider } from "@/contexts/WishlistContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import { CompareProvider } from "@/contexts/CompareContext";
import { CookieConsentProvider, CookieBanner } from "@/components/CookieBanner";
import { Toaster } from "@/components/ui/sonner";
import { PWAInstallBanner, registerServiceWorker } from "@/components/PWAInstall";

// Initialize Sentry (only if DSN is configured)
const SENTRY_DSN = process.env.REACT_APP_SENTRY_DSN;
if (SENTRY_DSN && SENTRY_DSN !== 'your_sentry_dsn_here') {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: process.env.NODE_ENV,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration(),
    ],
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });
}

// Pages
import { HomePage } from "@/pages/HomePageNetflix";
import { CartPage } from "@/pages/CartPage";
import { CheckoutPage } from "@/pages/CheckoutPage";
import { LegalPage } from "@/pages/LegalPage";
import { StaticPage } from "@/pages/StaticPage";
import { ProductDetailPage } from "@/pages/ProductDetailPage";
import { SearchPage } from "@/pages/SearchPage";
import { CategoryPage } from "@/pages/CategoryPage";
import { CategoriesListPage } from "@/pages/CategoriesListPage";
import { ReviewsPage } from "@/pages/ReviewsPage";
import { AdminTranslationsPage } from "@/pages/AdminTranslationsPage";
import { AdminDashboardPage } from "@/pages/AdminDashboardPage";
import { AdminOnboardingPage } from "@/pages/AdminOnboardingPage";
import { PreviewPage } from "@/pages/PreviewPage";
import { AdminBrandSettingsPage } from "@/pages/AdminBrandSettingsPage";
import { AdminPaymentsPage } from "@/pages/AdminPaymentsPage";
import { AdminSourcingPage } from "@/pages/AdminSourcingPage";
import { AdminAutomationPage } from "@/pages/AdminAutomationPage";
import { AdminSearchAnalyticsPage } from "@/pages/AdminSearchAnalyticsPage";
import { CheckoutSuccessPage } from "@/pages/CheckoutSuccessPage";
import { WishlistPage } from "@/pages/WishlistPage";
import { PublicWishlistPage } from "@/pages/PublicWishlistPage";
import { AdminEmailsPage } from "@/pages/AdminEmailsPage";
import { AdminEmailQueuePage } from "@/pages/AdminEmailQueuePage";
import { AdminFraudPage } from "@/pages/AdminFraudPage";
import { AdminBlacklistPage } from "@/pages/AdminBlacklistPage";
import { AdminAuditLogsPage } from "@/pages/AdminAuditLogsPage";
import { CrewJoinPage } from "@/pages/CrewJoinPage";
import { CrewDashboardPage } from "@/pages/CrewDashboardPage";
import { CrewVisualPage } from "@/pages/CrewVisualPage";
import { AdminCrewPage } from "@/pages/AdminCrewPage";
import { AdminAtmospheresPage } from "@/pages/AdminAtmospheresPage";
import { AdminSlidesPage } from "@/pages/AdminSlidesPage";
import { AdminNotificationsPage } from "@/pages/AdminNotificationsPage";
import { OrderDetailPage } from "@/pages/OrderDetailPage";
import { AdminOrderDetailPage } from "@/pages/AdminOrderDetailPage";
import { AdminOrdersPage } from "@/pages/AdminOrdersPage";
import { AdminPromotionsPage } from "@/pages/AdminPromotionsPage";
import { GiftCardPage } from "@/pages/GiftCardPage";
import { AdminSocialPage } from "@/pages/AdminSocialPage";
import { AdminSocialCalendarPage } from "@/pages/AdminSocialCalendarPage";
import { AdminSocialHistoryPage } from "@/pages/AdminSocialHistoryPage";
import { AdminAnalyticsPage } from "@/pages/AdminAnalyticsPage";
import { AdminABTestsPage } from "@/pages/AdminABTestsPage";
import { AdminPermissionsPage } from "@/pages/AdminPermissionsPage";
import { ComparePage } from "@/pages/ComparePage";
import { AdminNewsletterPage } from "@/pages/AdminNewsletterPage";
import { AdminBehaviorPage } from "@/pages/AdminBehaviorPage";
import { LoyaltyPage } from "@/pages/LoyaltyPage";
import { ChatSupportWidget } from "@/components/ChatSupportWidget";
// Phase 20 Pages
import { AdminProductionChecklistPage } from "@/pages/AdminProductionChecklistPage";
import { AdminHelpPage } from "@/pages/AdminHelpPage";
import { AdminReviewsQueuePage } from "@/pages/AdminReviewsQueuePage";
import { AdminImportWizardPage } from "@/pages/AdminImportWizardPage";
import { AdminSpreadsheetImportPage } from "@/pages/AdminSpreadsheetImportPage";
import { AdminCategoriesPage } from "@/pages/AdminCategoriesPage";
import { AdminProductEditPage } from "@/pages/AdminProductEditPage";
import { AdminBrandsMarqueePage } from "@/pages/AdminBrandsMarqueePage";
import { AdminPromoMarqueePage } from "@/pages/AdminPromoMarqueePage";
import { BundleDetailPage } from "@/pages/BundleDetailPage";
import { NotreSystemePage } from "@/pages/NotreSystemePage";
import { AdminNotreSystemePage } from "@/pages/AdminNotreSystemePage";
import { AdminAIPromptsPage } from "@/pages/AdminAIPromptsPage";
import { AdminAgentsPage } from "@/pages/AdminAgentsPage";
import AdminTelegramScraperPage from "@/pages/AdminTelegramScraperPage";
// Admin Theme Provider
import { AdminThemeProvider } from "@/contexts/AdminThemeContext";

// Wrapper pour les pages admin avec le thème
function AdminWrapper({ children }) {
  return <AdminThemeProvider>{children}</AdminThemeProvider>;
}

function App() {
  // Enregistrer le service worker au montage
  useEffect(() => {
    registerServiceWorker();
  }, []);

  return (
    <RTLWrapper>
      <ThemeProvider>
        <CookieConsentProvider>
          <StoreSettingsProvider>
            <AuthProvider>
              <CurrencyProvider>
                <CartProvider>
                  <WishlistProvider>
                    <CompareProvider>
                    <BrowserRouter>
                      <Routes>
                        <Route path="/" element={<HomePage />} />
                      <Route path="/products" element={<SearchPage />} />
                      <Route path="/product/:slug" element={<ProductDetailPage />} />
                      <Route path="/search" element={<SearchPage />} />
                      <Route path="/category/:slug" element={<CategoryPage />} />
                      <Route path="/categories/:slug" element={<CategoryPage />} />
                      <Route path="/categories" element={<CategoriesListPage />} />
                      <Route path="/cart" element={<CartPage />} />
                      <Route path="/checkout" element={<CheckoutPage />} />
                      
                      {/* Wishlist */}
                      <Route path="/account/wishlist" element={<WishlistPage />} />
                      <Route path="/wishlist/:shareToken" element={<PublicWishlistPage />} />
                      
                      {/* Compare */}
                      <Route path="/compare" element={<ComparePage />} />
                      
                      {/* Reviews */}
                      <Route path="/reviews" element={<ReviewsPage />} />
                      
                      {/* Loyalty (Phase 19) */}
                      <Route path="/account/loyalty" element={<LoyaltyPage />} />
                      
                      {/* Pages légales */}
                      <Route path="/cgv" element={<LegalPage />} />
                      <Route path="/terms" element={<LegalPage />} />
                      <Route path="/privacy" element={<LegalPage />} />
                      <Route path="/cookies" element={<LegalPage />} />
                      <Route path="/mentions-legales" element={<LegalPage />} />
                      <Route path="/legal-notice" element={<LegalPage />} />
                      <Route path="/legal/:pageType" element={<LegalPage />} />
                      {/* Pages statiques (créées via admin) */}
                      <Route path="/page/:slug" element={<StaticPage />} />
                      
                      {/* Bundle detail page */}
                      <Route path="/bundle/:id" element={<BundleDetailPage />} />
                      
                      {/* Page Notre Système */}
                      <Route path="/notre-systeme" element={<NotreSystemePage />} />
                      
                      {/* Admin pages - wrapped with AdminThemeProvider */}
                      <Route path="/admin" element={<AdminWrapper><AdminDashboardPage /></AdminWrapper>} />
                      <Route path="/admin/dashboard" element={<AdminWrapper><AdminDashboardPage /></AdminWrapper>} />
                      <Route path="/admin/onboarding" element={<AdminWrapper><AdminOnboardingPage /></AdminWrapper>} />
                      <Route path="/admin/translations" element={<AdminWrapper><AdminTranslationsPage /></AdminWrapper>} />
                      <Route path="/admin/settings/brand" element={<AdminWrapper><AdminBrandSettingsPage /></AdminWrapper>} />
                      <Route path="/admin/settings/payments" element={<AdminWrapper><AdminPaymentsPage /></AdminWrapper>} />
                      <Route path="/admin/settings/emails" element={<AdminWrapper><AdminEmailsPage /></AdminWrapper>} />
                      <Route path="/admin/sourcing" element={<AdminWrapper><AdminSourcingPage /></AdminWrapper>} />
                      <Route path="/admin/sourcing/import" element={<AdminWrapper><AdminImportWizardPage /></AdminWrapper>} />
                      <Route path="/admin/sourcing/spreadsheet" element={<AdminWrapper><AdminSpreadsheetImportPage /></AdminWrapper>} />
                      <Route path="/admin/automation" element={<AdminWrapper><AdminAutomationPage /></AdminWrapper>} />
                      <Route path="/admin/analytics/search" element={<AdminWrapper><AdminSearchAnalyticsPage /></AdminWrapper>} />
                      <Route path="/admin/emails/queue" element={<AdminWrapper><AdminEmailQueuePage /></AdminWrapper>} />
                      
                      {/* Security & Anti-fraud (Phase 10) */}
                      <Route path="/admin/security/fraud" element={<AdminWrapper><AdminFraudPage /></AdminWrapper>} />
                      <Route path="/admin/security/blacklist" element={<AdminWrapper><AdminBlacklistPage /></AdminWrapper>} />
                      <Route path="/admin/audit-logs" element={<AdminWrapper><AdminAuditLogsPage /></AdminWrapper>} />
                      
                      {/* Crew (Phase 11) */}
                      <Route path="/admin/crew" element={<AdminWrapper><AdminCrewPage /></AdminWrapper>} />
                      
                      {/* AI & Atmospheres (Phase 12) */}
                      <Route path="/admin/atmospheres" element={<AdminWrapper><AdminAtmospheresPage /></AdminWrapper>} />
                      <Route path="/admin/slides" element={<AdminWrapper><AdminSlidesPage /></AdminWrapper>} />
                      <Route path="/admin/ai-prompts" element={<AdminWrapper><AdminAIPromptsPage /></AdminWrapper>} />
                      <Route path="/admin/agents" element={<AdminWrapper><AdminAgentsPage /></AdminWrapper>} />
                      <Route path="/admin/telegram-scraper" element={<AdminWrapper><AdminTelegramScraperPage /></AdminWrapper>} />
                      
                      {/* Notifications (Phase 13) */}
                      <Route path="/admin/notifications" element={<AdminWrapper><AdminNotificationsPage /></AdminWrapper>} />
                      
                      {/* Admin Order Detail (Phase 14) */}
                      <Route path="/admin/orders" element={<AdminWrapper><AdminOrdersPage /></AdminWrapper>} />
                      <Route path="/admin/orders/:orderNumber" element={<AdminWrapper><AdminOrderDetailPage /></AdminWrapper>} />
                      
                      {/* Admin Promotions (Phase 15) */}
                      <Route path="/admin/promotions" element={<AdminWrapper><AdminPromotionsPage /></AdminWrapper>} />
                      
                      {/* Admin Social Studio (Phase 16) */}
                      <Route path="/admin/social" element={<AdminWrapper><AdminSocialPage /></AdminWrapper>} />
                      <Route path="/admin/social/calendar" element={<AdminWrapper><AdminSocialCalendarPage /></AdminWrapper>} />
                      <Route path="/admin/social/history" element={<AdminWrapper><AdminSocialHistoryPage /></AdminWrapper>} />
                      
                      {/* Admin Analytics (Phase 17) */}
                      <Route path="/admin/analytics" element={<AdminWrapper><AdminAnalyticsPage /></AdminWrapper>} />
                      <Route path="/admin/analytics/ab-tests" element={<AdminWrapper><AdminABTestsPage /></AdminWrapper>} />
                      <Route path="/admin/settings/permissions" element={<AdminWrapper><AdminPermissionsPage /></AdminWrapper>} />
                      
                      {/* Admin Engagement (Phase 19) */}
                      <Route path="/admin/newsletter" element={<AdminWrapper><AdminNewsletterPage /></AdminWrapper>} />
                      <Route path="/admin/analytics/behavior" element={<AdminWrapper><AdminBehaviorPage /></AdminWrapper>} />
                      
                      {/* Phase 20 — Production Ready */}
                      <Route path="/admin/settings/production-checklist" element={<AdminWrapper><AdminProductionChecklistPage /></AdminWrapper>} />
                      <Route path="/admin/help" element={<AdminWrapper><AdminHelpPage /></AdminWrapper>} />
                      <Route path="/admin/reviews/queue" element={<AdminWrapper><AdminReviewsQueuePage /></AdminWrapper>} />
                      
                      {/* Marketing Pages */}
                      <Route path="/admin/pages/notre-systeme" element={<AdminWrapper><AdminNotreSystemePage /></AdminWrapper>} />
                      
                      {/* Categories & Product Edit */}
                      <Route path="/admin/categories" element={<AdminWrapper><AdminCategoriesPage /></AdminWrapper>} />
                      <Route path="/admin/products/:productId" element={<AdminWrapper><AdminProductEditPage /></AdminWrapper>} />
                      
                      {/* Marketing Banners */}
                      <Route path="/admin/brands-marquee" element={<AdminWrapper><AdminBrandsMarqueePage /></AdminWrapper>} />
                      <Route path="/admin/promo-marquee" element={<AdminWrapper><AdminPromoMarqueePage /></AdminWrapper>} />
                      
                      {/* Checkout */}
                      <Route path="/checkout/success" element={<CheckoutSuccessPage />} />
                      
                      {/* Order Detail (Phase 14) */}
                      <Route path="/order/:orderNumber" element={<OrderDetailPage />} />
                      
                      {/* Gift Cards (Phase 15) */}
                      <Route path="/gift-cards" element={<GiftCardPage />} />
                      
                      {/* Crew Public Pages */}
                      <Route path="/crew/join" element={<CrewJoinPage />} />
                      <Route path="/crew/dashboard" element={<CrewDashboardPage />} />
                      <Route path="/crew/visual/:templateId" element={<CrewVisualPage />} />
                      
                      {/* Preview mode */}
                      <Route path="/preview" element={<PreviewPage />} />
                    </Routes>
                    <CookieBanner />
                    <PWAInstallBanner />
                    <ChatSupportWidget />
                  </BrowserRouter>
                  <Toaster position="bottom-right" />
                    </CompareProvider>
                </WishlistProvider>
              </CartProvider>
            </CurrencyProvider>
          </AuthProvider>
        </StoreSettingsProvider>
      </CookieConsentProvider>
      </ThemeProvider>
    </RTLWrapper>
  );
}

export default App;
